import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { mergeDeepLeft } from 'ramda';
import { createHashHistory } from 'history';
import { startCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import * as ajax from './ajax';
import changeActionFactory from './factories/changeActionFactory';
import contentChangeActionFactory from './factories/contentChangeActionFactory';
import entityActionFactory from './factories/entityActionFactory';
import uploadActionFactory from './factories/uploadActionFactory';
import { constantsRegistry } from '../constants/tableConstantsFactory';
import { evaluate } from '../../behaviors/src/index';
import { evaluateSource } from '../../behaviors/src/lib/evaluateSource';
import { nativeBehaviorEvaluator } from '../nativeBehaviors/nativeBehaviorEvaluator';
import { configBehaviorsFunc } from '../nativeBehaviors/configBehaviors';
import { checkPermission, makeResource } from '../../behaviors/src/lib/permissions';
import tokenErrorActions from './tokenErrorActionCreators';
import { legacyRectStringToFormRect } from '../utils/transformFormAnnotation';
import annTools from '../components/common/pdf/annotations/commonAnnotationTools';
import createUtils from '../components/reportDocuments/create';
import formConstants from '../constants/formConstants';

// define the basic actions, and include page and change actions.
const changeActions = changeActionFactory('form', 'forms');
const entityActions = entityActionFactory('form', 'forms');
const uploadActions = uploadActionFactory('form', 'forms', true, 'Photo');
const contentChangeActions = contentChangeActionFactory('form', 'forms');
const constants = constantsRegistry('form');
const hashHistory = createHashHistory();
const batchChange = changesArray => async dispatch => {
  dispatch({
    type: constants.BATCH_CHANGE,
    payload: changesArray
  });
};
const setInitialActive = initialActive => ({
  type: constants.SET_INITIAL_ACTIVE,
  initialActive
});
const create = (id, createdDate, appCreatorID, formType) => async dispatch => {
  // create the data to store in state, prior to the user clicking "Save". Note this works
  // differently than reportbuilder, which saves to the server immediately
  let format = formType.formatInput;
  if (typeof format === 'string') {
    format = JSON.parse(formType.formatInput);
  }
  const _format = format,
    version = _format.version;
  const defaultData = {
    appCreated: createdDate,
    appCreatorID,
    content: {
      id,
      version
    },
    format,
    id,
    isNew: true,
    staticContent: formType.staticContent ? JSON.parse(formType.staticContent) : {}
  };
  await dispatch(changeActions.create(id, defaultData));
  let initialContent = null;
  try {
    initialContent = JSON.parse(formType.initialContent);
  } catch (e) {
    // no op
  }
  const isLegacyJob = () => formType.identifier === 'job' && JSON.stringify(format).includes('jobGeneralSubsection');
  // create content
  const content = _objectSpread(_objectSpread({}, initialContent), {}, {
    // add initial content
    id,
    // Avoid setting formTypeID in the case of jobs since this is set on the server. For normal
    // forms formTypeID should be included to signify to the server to create a new record.
    // Including for jobs results in: https://www.pivotaltracker.com/story/show/183591508.
    formTypeID: isLegacyJob() ? null : formType.id,
    formType: {
      identifier: formType.identifier,
      name: formType.name
    },
    version
  });

  // prepare the mergePatch for eventual save
  let contentChange = {
    id,
    content
  };
  const evaluator = nativeBehaviorEvaluator.bind(null, defaultData, 'form');
  const nativeFunctions = {
    errorHandler: err => {
      throw err;
    },
    evaluator,
    configBehaviors: configBehaviorsFunc
  };

  // run create behaviors.
  const createPatch = await evaluate('create', content, defaultData.format, nativeFunctions, 'web');
  contentChange = mergeDeepLeft({
    content: createPatch
  }, contentChange);

  // run view behaviors.
  const viewPatch = await evaluate('view', content, defaultData.format, nativeFunctions, 'web');
  contentChange = mergeDeepLeft({
    content: viewPatch
  }, contentChange);

  // store the initial "create" data, in case of failure during Job creation
  await dispatch(setInitialActive(_objectSpread(_objectSpread({}, defaultData), {}, {
    content: contentChange.content
  })));
  const result = await dispatch(contentChangeActions.changeContent(id, contentChange));
  await dispatch(contentChangeActions.formatChange(id)); // initialize format
  return result;
};

/**
 * Evaluate annotationHandler.create behavior with a proposed content
 * This is a generic function used by both evaluateAnnotationHandlerColor and createFormAnnotation
 * @param {Object} annotationHandler - Source behaviors to evaluate
 * @param {Object} parent - Parent content and path
 * @param {Object} proposedContent - Proposed content to evaluate
 * @param {string} formTypeIdentifier - Form type identifier
 * @returns {Promise<Object>} Evaluated behavior content or empty object
 */
const evaluateAnnotationHandlerCreate = async (annotationHandler, parent, proposedContent, formTypeIdentifier) => {
  if (!annotationHandler || !annotationHandler.create || !parent) {
    return {};
  }
  try {
    const evaluator = nativeBehaviorEvaluator.bind(null, {}, 'form');
    const nativeFunctions = {
      errorHandler: err => {
        throw err;
      },
      evaluator,
      configBehaviors: configBehaviorsFunc
    };

    // Evaluate the create behavior with the proposed content
    const evaluatedBehavior = await evaluateSource(annotationHandler.create, parent.content, parent.contentPath, {
      proposedContent,
      formTypeIdentifier
    },
    // transient context
    nativeFunctions);
    return (evaluatedBehavior === null || evaluatedBehavior === void 0 ? void 0 : evaluatedBehavior.content) || {};
  } catch (error) {
    // Silently fail - return empty object
    return {};
  }
};

/**
 * Create a new form annotation and add it to the form state
 * @param {Object} clickDetails - Details from the PDF click event
 * @param {string} selectableType - Type of annotation to create
 * @param {string} defaultColor - Default color for the annotation
 * @param {Object} annotationHandler - Source behaviors to evaluate at creation time
 * @returns {Function} Redux thunk action
 */
const createFormAnnotation = (clickDetails, selectableType, annotationHandler, parent, _ref // this object is intended to support any number of additional options
) => {
  let defaultColor = _ref.defaultColor;
  return async (dispatch, getState) => {
    var _state$login;
    if (!selectableType || !clickDetails) {
      return;
    }

    // Permission check at the start
    const state = getState();
    const user = (_state$login = state.login) === null || _state$login === void 0 ? void 0 : _state$login.user;
    if (user && user.permissions) {
      const _selectableType$split = selectableType.split('__'),
        _selectableType$split2 = _slicedToArray(_selectableType$split, 2),
        formTypeIdentifier = _selectableType$split2[1];
      if (formTypeIdentifier) {
        const resource = makeResource('form', formTypeIdentifier);
        const hasPermission = checkPermission(resource, 'create', user.permissions, user);
        if (hasPermission !== true) {
          return; // Exit early without creating annotation
        }
      }
    }

    // Generate a new annotation ID
    const id = uuidv4();
    // Extract the base type from compound keys (e.g., 'ink__someIdentifier' -> 'ink')
    const _selectableType$split3 = selectableType.split('__'),
      _selectableType$split4 = _slicedToArray(_selectableType$split3, 2),
      renderType = _selectableType$split4[0],
      formTypeIdentifier = _selectableType$split4[1];

    // Get page, rotation, etc. from clickDetails
    const pageNumber = clickDetails.pageNumber,
      _clickDetails$rotatio = clickDetails.rotation,
      rotation = _clickDetails$rotatio === void 0 ? 0 : _clickDetails$rotatio,
      rectString = clickDetails.rectString;

    // Type-specific overrides that extend the default annotation
    const typeOverrides = {
      freeText: details => {
        const boxHeight = 60;
        const boxWidth = 60;
        const xLocWithOffset = details.xLocation + 2;
        const yLocWithOffset = details.yLocation - boxHeight;
        return {
          rect: {
            origin: {
              x: xLocWithOffset,
              y: yLocWithOffset
            },
            size: {
              width: boxWidth,
              height: boxHeight
            }
          },
          color: {
            red: 1,
            green: 1,
            blue: 1,
            alpha: 0
          },
          textColor: defaultColor ? annTools.toRGBAObject(defaultColor) : {
            red: 1,
            green: 0,
            blue: 0
          },
          // use defaultColor if available, otherwise red
          text: 'Text',
          fontName: 'Helvetica',
          fontSize: 14,
          alpha: 1 // matches iPad
        };
      },
      highlight: details => {
        // For highlight annotations, we need both rectArray and rect
        let rectArray = [];
        if (details.rectsString) {
          rectArray = details.rectsString.split('/').map(legacyRectStringToFormRect).filter(Boolean);
        } else if (details.rectString) {
          const parsedRect = legacyRectStringToFormRect(details.rectString);
          if (parsedRect) {
            rectArray = [parsedRect];
          }
        }

        // Highlight annotations require text selection data (rectsString or rectString)
        // If we don't have valid rects, return null to prevent creation
        if (rectArray.length === 0) {
          return null;
        }

        // Use the first rect for the main rect property
        const rect = rectArray[0];
        return {
          rect,
          rectArray,
          alpha: 0.5,
          // Always use 0.5 alpha for highlight annotations
          color: defaultColor ? annTools.toRGBAObject(defaultColor) : {
            red: 1,
            green: 1,
            blue: 0
          } // use defaultColor if available, otherwise yellow
        };
      },
      ink: details => {
        const pointsToUse = details.points;
        if (!pointsToUse || !pointsToUse.length) return null;

        // Process points similar to report annotations
        const drawnAnnotationArrays = annTools.groupPointsByPage(pointsToUse);
        if (drawnAnnotationArrays.length === 0) return null;

        // Use the first (and should be only) annotation array
        const pts = drawnAnnotationArrays[0];
        if (pts.length < 2) return null; // Need at least 2 points for a line

        // Convert canvas coordinates to PDF coordinates for storage
        const pointArray = pts.map(point => {
          const pageNum = point.pageNumber || details.pageNumber;
          const pdfPoint = annTools.convertPointToPDFCoords(point, pageNum);
          return {
            x: pdfPoint.x,
            y: pdfPoint.y
          };
        });
        return {
          alpha: 0.5,
          pointArray,
          strokeWidth: details.strokeWidth || 2,
          unsmoothed: 0,
          color: defaultColor ? annTools.toRGBAObject(defaultColor) : {
            red: 0,
            green: 0,
            blue: 0
          } // use defaultColor if available, otherwise black
        };
      },
      line: details => {
        const pointsToUse = details.points;
        if (!pointsToUse || !pointsToUse.length) return null;

        // Process points similar to report annotations
        const drawnAnnotationArrays = annTools.groupPointsByPage(pointsToUse);
        if (drawnAnnotationArrays.length === 0) return null;

        // Use the first (and should be only) annotation array
        const pts = drawnAnnotationArrays[0];
        if (pts.length < 2) return null; // Need at least 2 points for a line

        // For lines, only use first and last points
        const truePath = [pts[0], pts[pts.length - 1]];

        // Convert canvas coordinates to PDF coordinates for storage
        const pointArray = truePath.map(point => {
          const pageNum = point.pageNumber || details.pageNumber;
          const pdfPoint = annTools.convertPointToPDFCoords(point, pageNum);
          return {
            x: pdfPoint.x,
            y: pdfPoint.y
          };
        });
        const finalWidth = details.strokeWidth || 8;
        return {
          alpha: 0.5,
          pointArray,
          // Use lineThickness (8) as default, same as report context
          strokeWidth: finalWidth,
          unsmoothed: 1,
          color: defaultColor ? annTools.toRGBAObject(defaultColor) : {
            red: 0,
            green: 0,
            blue: 0
          } // use defaultColor if available, otherwise black
        };
      },
      info: details => {
        // saved radius is 15px, full size in the rectString should be 30,30
        const infoRadius = 15;
        const xLocWithOffset = details.xLocation - infoRadius;
        const yLocWithOffset = details.yLocation - infoRadius;
        return {
          rect: {
            origin: {
              x: xLocWithOffset,
              y: yLocWithOffset
            },
            size: {
              width: 30,
              height: 30
            }
          },
          color: defaultColor ? annTools.toRGBAObject(defaultColor) : {
            red: 0,
            green: 1,
            blue: 0
          },
          // use defaultColor if available, otherwise green
          text: '',
          alpha: 1 // matches iPad
        };
      },
      note: details => {
        // saved radius is 15px, full size in the rectString should be 30,30
        const noteRadius = 15;
        const xLocWithOffset = details.xLocation - noteRadius;
        const yLocWithOffset = details.yLocation - noteRadius;
        return {
          rect: {
            origin: {
              x: xLocWithOffset,
              y: yLocWithOffset
            },
            size: {
              width: 30,
              height: 30
            }
          },
          color: defaultColor ? annTools.toRGBAObject(defaultColor) : {
            red: 1,
            green: 0.5,
            blue: 0 // use defaultColor if available, otherwise orange
          },
          textColor: {
            red: 1,
            green: 1,
            blue: 1 // white text color by default
          },
          text: 'Note',
          alpha: 1 // matches iPad
        };
      }
    };

    // Get the type override first to check if annotation should be created
    const typeOverride = typeOverrides[renderType](clickDetails);
    if (typeOverride === null) {
      return; // Exit early without creating the annotation
    }

    // Create the new render object so that it can be passed to the annotationHandler.create behavior
    const newRender = _objectSpread(_objectSpread(_objectSpread({
      renderType: renderType.charAt(0).toUpperCase() + renderType.slice(1)
    }, (renderType === 'ink' || renderType === 'line') && clickDetails.points && clickDetails.points.length ? {
      rect: (() => {
        const drawnAnnotationArrays = annTools.groupPointsByPage(clickDetails.points);
        if (drawnAnnotationArrays.length === 0) return legacyRectStringToFormRect(rectString);
        const pts = drawnAnnotationArrays[0];
        if (pts.length < 2) return legacyRectStringToFormRect(rectString);
        const truePath = renderType === 'line' ? [pts[0], pts[pts.length - 1]] : pts;
        // Use the pageNumber from the points, not from clickDetails
        const pointsPageNumber = pts[0] && pts[0].pageNumber ? pts[0].pageNumber : pageNumber;
        const pathString = createUtils.pathStringFromCanvasCoords(truePath, pointsPageNumber);
        const rectStringFromPath = annTools.recalcRectStringFromPath(pathString);
        return legacyRectStringToFormRect(rectStringFromPath);
      })(),
      page: (() => {
        const drawnAnnotationArrays = annTools.groupPointsByPage(clickDetails.points);
        if (drawnAnnotationArrays.length === 0) return pageNumber;
        const pts = drawnAnnotationArrays[0];
        return pts[0] && pts[0].pageNumber ? pts[0].pageNumber : pageNumber;
      })()
    } : {
      rect: legacyRectStringToFormRect(rectString),
      page: pageNumber
    }), {}, {
      rotation
    }, renderType !== 'freeText' ? {
      color: annTools.toRGBAObject(defaultColor)
    } : {}), typeOverride);

    // Evaluate annotationHandler behaviors at creation time
    // Use the shared helper function to avoid code duplication
    const proposedContent = {
      render: newRender
    };
    const evaluatedCreateBehavior = await evaluateAnnotationHandlerCreate(annotationHandler, parent, proposedContent, formTypeIdentifier);

    // Create the primary form entity data
    const createPatch = {
      id,
      entity: 'form',
      patch: _objectSpread({
        id,
        formTypeIdentifier,
        // used instead of formTypeID for creation. Will be translated in server
        formType: {
          identifier: formTypeIdentifier,
          name: startCase(formTypeIdentifier) // create display name without looking up in formType
        },
        render: newRender
      }, evaluatedCreateBehavior)
    };
    dispatch(contentChangeActions.serverSaveMergePatches(id, [createPatch], (savedID, savedResponses) => () => {
      // The annotation is now saved to the server
      // Add the annotation to the annotation state by dispatching to the annotation reducer
      // This will add it to groupedAnnotations and make it visible in the UI
      const savedAnnotation = savedResponses[0];
      dispatch({
        type: 'ANNOTATION_CREATE_SUCCESS',
        payload: {
          annotation: savedAnnotation
        }
      });
    }));
  };
};
const handleRedirect = (dispatch, getState) => {
  const entityState = getState().form;
  dispatch(entityActions.serverIndex(entityState.indexQuery, true, true));
  hashHistory.goBack();
};
const includedActions = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, changeActions), entityActions), contentChangeActions), uploadActions);
const render = () => ({
  type: constants.RENDER
});

// This could be a show success, however show success performs extra state
// changes, all renderSuccess need do is replace the active entity with the
// new one returned from the server
const renderSuccess = data => ({
  type: constants.RENDER_SUCCESS,
  data
});
const renderFailure = formID => ({
  type: constants.RENDER_FAILURE,
  formID
});
const serverRenderForm = formID => async (dispatch, getState) => {
  const url = "/forms/render/".concat(formID);
  dispatch(render());
  try {
    const response = await ajax.get(url);
    return dispatch(renderSuccess(response.data));
  } catch (e) {
    const failureAction = () => {
      // must check for error response, internal and network errors have no 'response'
      if (e.response && e.response.status === 410) {
        // 410 === 'Form Not Found' / GoneError
        const _getState = getState(),
          form = _getState.form;
        // re-fetch forms since the requested form has been deleted and may still be visible
        // in the file cabinet: https://www.pivotaltracker.com/story/show/160912438
        dispatch(entityActions.serverIndex(form.indexQuery, true, true));
      }
      return dispatch(renderFailure(formID));
    };
    return dispatch(tokenErrorActions.handleError(e, 'form', failureAction, serverRenderForm.bind(this, formID), handleRedirect));
  }
};

/**
 *  Override entityActionFactory.serverIndex to add filter for selected form
 */
const serverIndex = (formTypeIdentifiers, indexQuery) => async dispatch => {
  const formIndexQuery = _objectSpread(_objectSpread({}, indexQuery), {}, {
    filters: _objectSpread(_objectSpread({}, indexQuery.filters), {}, {
      formTypeIdentifiers
    })
  });
  return dispatch(entityActions.serverIndex(formIndexQuery));
};

/**
 *  Normal serverShow operation will dispatch a detailActionFactory.doesNotExist error, but for
 *  the current form usage that's attached to jobs, let the job handle the failure
 */
const failureFunc = () => ({
  type: constants.ERROR_DOES_NOT_EXIST
});
const serverShow = id => contentChangeActions.serverShow(id, null, null, failureFunc);

/**
 * Delete a form using the forms endpoint with query parameters
 * This follows the same pattern as bulkDelete in cabinetActionCreators
 */
const serverDelete = id => async dispatch => {
  try {
    await ajax.remove('/forms', {
      ids: [id]
    });
    return dispatch(changeActions.destroySuccess(id));
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', changeActions.destroyFail.bind(this, id, e), serverDelete.bind(this, id)));
  }
};

// Override undoChanges to pass the reverted form state for annotation reducer
const undoChanges = () => (dispatch, getState) => {
  const currentState = getState().form;
  const originalActive = currentState.originalActive;

  // First dispatch the undo action to revert the form state
  dispatch(changeActions.undoChanges());

  // Then dispatch an action with the reverted form state for other reducers to use
  if (originalActive && originalActive.content) {
    dispatch({
      type: "".concat(constants.UNDO_CHANGES, "_WITH_STATE"),
      payload: {
        revertedFormState: originalActive
      }
    });
  }
};
const formActions = _objectSpread(_objectSpread({}, includedActions), {}, {
  batchChange,
  create,
  renderFailure,
  serverDelete,
  serverIndex,
  serverRenderForm,
  serverShow,
  undoChanges // Override the default undoChanges
});

/**
 * serverRenderAnnotatedForm - Download annotated form PDF
 * @param formID
 * @param remotePath
 * @param formTypeIdentifiers
 * @param filters
 */
const serverRenderAnnotatedForm = (formID, remotePath, formTypeIdentifiers, filters) => async dispatch => {
  const url = "/formDocuments/render/".concat(formID);
  const additionalData = {
    formID,
    annotateDocument: true,
    remotePath,
    formTypeIdentifiers,
    filters
  };
  try {
    const response = await ajax.get(url, additionalData);
    if (response.data && response.data.pdfPath) {
      return dispatch(formActions.renderAnnotatedSuccess(response.data));
    }
    return dispatch(formActions.renderAnnotatedFailure(formID));
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', formActions.renderAnnotatedFailure.bind(this, formID), formActions.serverRenderAnnotatedForm.bind(this, formID, remotePath, formTypeIdentifiers, filters)));
  }
};

// set the pdfPath for download
const renderAnnotatedSuccess = _ref2 => {
  let pdfPath = _ref2.pdfPath;
  return {
    type: formConstants.RENDER_ANNOTATED_SUCCESS,
    data: {
      downloadPDFPath: pdfPath
    }
  };
};

// update error state
const renderAnnotatedFailure = formID => ({
  type: formConstants.RENDER_ANNOTATED_FAILURE,
  data: {
    formID
  }
});

// clear the download pdfPath
const clearDownloadPDFPath = () => ({
  type: formConstants.CLEAR_DOWNLOAD_PATH
});
formActions.createFormAnnotation = createFormAnnotation;
formActions.serverRenderAnnotatedForm = serverRenderAnnotatedForm;
formActions.renderAnnotatedSuccess = renderAnnotatedSuccess;
formActions.renderAnnotatedFailure = renderAnnotatedFailure;
formActions.clearDownloadPDFPath = clearDownloadPDFPath;
export { evaluateAnnotationHandlerCreate };
export default formActions;